/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import apiService from '../../../../share/util/api/service';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { executeSyncRequest } from '../../../../share/util/api/syncRequest';
import { PHASE, USER } from './constants';

export default {
  name: 'requirement170',
  data() {
    return {
      systemId: '',
      displayInfo: '',
      isLoading: false,
      phase: PHASE.START,
      twelveDigitModelCode: '',
      validateAppliedModel: undefined,
      validateOptionCode: undefined,
      chkValidateAppliedModel: false,
      chkValidateOptionCode: false,
      variantData: [],
      inputAppliedModel: '',
      inputOptionCode: '',
      rowIndex: 0,
      modal_requirement_170: false,
      lastEvaluatedKey: null,
      isSession: false,
      isExecutionStarted: false,
      isExiting: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログヘッダータイトル */
    headerTitle() {
      const titleMap = {
        [PHASE.START]: 'message.common_title_confirmation',
        [PHASE.INPUT_DATA]: 'message.common_title_confirmation',
        [PHASE.WRITING]: 'individual.common_title_execution',
        [PHASE.SUCCESS]: 'message.common_title_success',
        [PHASE.INVALID_DATA]: 'message.common_title_error',
        [PHASE.WRITE_ERROR]: 'message.common_title_error',
        [PHASE.FAILED]: 'message.common_title_error',
      };

      return this.$t(titleMap[this.phase]);
    },

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.INVALID_DATA:
        case PHASE.WRITE_ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.INPUT_DATA:
        case PHASE.WRITING:
        default:
          return 'secondary';
      }
    },

    /** アプライドモデル・オプションコード入力画面での入力値 */
    inputData() {
      return [this.inputAppliedModel, this.inputOptionCode];
    },

    /** 入力画面表示フラグ */
    isInputPhase() {
      return this.phase === PHASE.INPUT_DATA;
    },

    /** 本文メッセージ */
    workSupportMessage() {
      if (this.phase === PHASE.SUCCESS) {
        return this.displayInfo;
      }

      const messageMap = {
        [PHASE.START]: `${this.i18nWsKey}.REQ_WS_170_TXT_1`,
        [PHASE.WRITING]: `${this.i18nWsKey}.REQ_WS_170_TXT_2`,
        [PHASE.INVALID_DATA]: `${this.i18nWsKey}.REQ_WS_170_TXT_4`,
        [PHASE.WRITE_ERROR]: `${this.i18nWsKey}.REQ_WS_170_TXT_5`,
        [PHASE.FAILED]: 'individual.error_communication_error',
      };

      return this.$t(messageMap[this.phase] || `${this.i18nWsKey}.REQ_WS_170_TXT_1`);
    },

    /** キャンセルボタン表示 */
    showButtonCancel() {
      return this.phase === PHASE.START || this.phase === PHASE.INPUT_DATA;
    },

    /** OKボタン表示 */
    showButtonOk() {
      return this.phase === PHASE.START
        || this.phase === PHASE.INPUT_DATA
        || this.phase === PHASE.INVALID_DATA
        || this.phase === PHASE.SUCCESS
        || this.phase === PHASE.WRITE_ERROR
        || this.phase === PHASE.FAILED;
    },

    /** OKボタン活性 */
    isOkDisabled() {
      if (this.phase === PHASE.INPUT_DATA) {
        return this.isLoading || !this.chkValidateAppliedModel || !this.chkValidateOptionCode;
      }
      return this.isLoading;
    }
  },

  watch: {
    inputData() {
      this.inputDataValidate();
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    init() {
      this.validateAppliedModel = undefined;
      this.validateOptionCode = undefined;
      this.modal_requirement_170 = true;
      this.isExecutionStarted = false;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_170 = false;
    },

    async showUnexpectedError() {
      if (this.phase === PHASE.FAILED) {
        return;
      }
      if (this.isExecutionStarted) {
        await this.exitProcess();
      }
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result handler.validate で返ってきたバリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    validateErrorResult(result, calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        (self) => { self; this.logout(this); },
        (self) => { self; this.resetSession(this); },
        (result) => {
          result;
          if (calledFrom === 'terminate' || calledFrom === 'dispose') {
            return;
          }
          this.showUnexpectedError();
        },
      );
    },

    /** バリアントデータ取得 */
    async getVariantData() {
      try {
        this.isLoading = true;
        const response = await executeSyncRequest(
          this,
          async () => {
            return await apiService.getParamCall(
              apiService.WSP,
              '/variant/netApps/' + this.netAppId + '/systems/' + this.systemId,
              {
                last_evaluated_key: this.lastEvaluatedKey ? JSON.stringify(this.lastEvaluatedKey) : null
              }
            );
          },
          (errorType) => { errorType; this.showUnexpectedError(); },
          (getApiResponse) => { getApiResponse; return ''; },
          (result) => { this.validateErrorResult(result); },
        );

        if (response?.data?.variant_data_list && response.data.variant_data_list.length !== 0) {
          this.variantData = this.variantData.concat(response.data.variant_data_list);
          this.twelveDigitModelCode = response.data.twelve_digit_model_code;

          if (response.data.last_evaluated_key) {
            this.lastEvaluatedKey = response.data.last_evaluated_key;
            await this.getVariantData();
          } else if (this.twelveDigitModelCode) {
            this.inputAppliedModel = this.twelveDigitModelCode.slice(0, 7);
            this.inputOptionCode = this.twelveDigitModelCode.slice(7).trim();
          }
        } else {
          await this.showUnexpectedError();
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 入力バリデーション */
    inputDataValidate() {
      this.chkValidateAppliedModel = this.checkValidateAppliedModel();
      this.chkValidateOptionCode = this.checkValidateOptionCode();
    },

    /** 入力値とバリアントデータ照合 */
    async validateWithVariantData() {
      if (!this.chkInputParam(this.inputAppliedModel, this.inputOptionCode, this.variantData)) {
        this.phase = PHASE.INVALID_DATA;
        return;
      }
      this.phase = PHASE.WRITING;
      await this.execute();
    },

    /**
     * 入力値と variant_data_list の一致判定
     * @param {string} inputAppliedModel アプライドモデルの入力値
     * @param {string} inputOptionCode オプションコードの入力値
     * @param {Array} variantData 取得したバリアントデータ群
     * @returns {boolean} true: 一致 / false: 不一致
     */
    chkInputParam(inputAppliedModel, inputOptionCode, variantData) {
      let idx = 0;
      let chkFlg = false;

      while (idx < variantData.length) {
        const optionCode = variantData[idx].destination_code + variantData[idx].op_code;
        if (inputAppliedModel.toUpperCase() === variantData[idx].applied_model.toUpperCase()) {
          const patternString = optionCode.replace(/_/g, '').toUpperCase();
          const regexPattern = new RegExp('^' + patternString.replace(/\*/g, '.?') + '$');
          if (regexPattern.test(inputOptionCode.toUpperCase())) {
            chkFlg = true;
            this.rowIndex = idx;
            break;
          }
        }
        idx++;
      }
      return chkFlg;
    },

    /** アプライドモデルのバリデーション */
    checkValidateAppliedModel() {
      this.validateAppliedModel = undefined;
      if (!this.inputAppliedModel) {
        this.validateAppliedModel = { valid: true, msg: this.$t('message.alert_validate_required') };
        return false;
      }
      if (this.checkInvalidCharacter(this.inputAppliedModel)) {
        this.validateAppliedModel = { valid: true, msg: this.$t('message.alert_validate_invalid_pattern') };
        return false;
      }
      if (this.inputAppliedModel.length !== 7) {
        this.validateAppliedModel = { valid: true, msg: this.$t('message.alert_validate_max_length', ['7']) };
        return false;
      }
      return true;
    },

    /** オプションコードのバリデーション */
    checkValidateOptionCode() {
      this.validateOptionCode = undefined;
      if (this.checkInvalidCharacter(this.inputOptionCode)) {
        this.validateOptionCode = { valid: true, msg: this.$t('message.alert_validate_invalid_pattern') };
        return false;
      }
      if (!this.chkValidateAppliedModel) {
        return true;
      }

      const fifthChar = this.inputAppliedModel.slice(4, 5);
      const regExpNum = /^([0-9]+)$/i;
      if (regExpNum.test(fifthChar)) {
        if (this.inputOptionCode.length > 3) {
          this.validateOptionCode = { valid: true, msg: this.$t('message.alert_validate_max_length', ['3']) };
          return false;
        }
      } else if (this.inputOptionCode.length > 4) {
        this.validateOptionCode = { valid: true, msg: this.$t('message.alert_validate_max_length', ['4']) };
        return false;
      }
      return true;
    },

    /**
     * 使用不可な文字列が含まれていないかの確認関数
     * @param {string} code 検査対象の文字列
     * @returns {boolean} true：不正な文字あり / false：問題なし
     */
    checkInvalidCharacter(code) {
      const halfReqExp = new RegExp(/^[a-zA-Z0-9]*$/);
      return !halfReqExp.test(code);
    },

    /**
     * API呼び出し
     * @param {string} type APIの種類
     * @param {object} executeParams 実行時の追加パラメータ
     * @returns pollingWspApiの実行結果
     */
    async callApi(type, executeParams = null) {
      const token = session.getToken();
      const body = { id_token: token.id_token, type };
      if (executeParams) Object.assign(body, executeParams);
      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        body,
        (errorType) => {
          errorType;
          if (type === 'terminate' || type === 'dispose') return;
          this.showUnexpectedError();
        },
        (result) => { this.validateErrorResult(result, type); },
        (result) => { this.validateErrorResult(result, type); },
      );
    },

    /** 実行処理 */
    async execute() {
      try {
        this.isLoading = true;
        const response = await this.callApi('execute', {
          applied_model: this.variantData[this.rowIndex].applied_model,
          destination_code: this.variantData[this.rowIndex].destination_code,
          op_code: this.variantData[this.rowIndex].op_code,
          development_code: this.variantData[this.rowIndex].development_code,
        });
        if (!response) {
          return;
        }

        this.isSession = response.data?.user_data?.is_session ?? true;
        this.isExecutionStarted = true;
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          if (response.data.user_data?.result === 'ok') {
            let replaceMessage = this.$t(`${this.i18nWsKey}.REQ_WS_170_TXT_3`)
              .replace(USER, this.variantData[this.rowIndex].applied_model);
            replaceMessage = replaceMessage.replace(
              USER,
              this.variantData[this.rowIndex].destination_code + this.variantData[this.rowIndex].op_code
            );
            this.displayInfo = replaceMessage;
            await this.terminate();
            this.phase = PHASE.SUCCESS;
            return;
          }
          if (response.data.user_data?.result === 'ng') {
            await this.terminate();
            this.phase = PHASE.WRITE_ERROR;
            return;
          }
          await this.showUnexpectedError();
          return;
        }

        if (response.data.status === REQUEST_STATUS_FAILED) {
          await this.showUnexpectedError();
          return;
        }

        await this.showUnexpectedError();
      } finally {
        this.isLoading = false;
      }
    },

    /** 入力画面へ遷移 */
    inputDataProcess() {
      this.phase = PHASE.INPUT_DATA;
    },

    /** キャンセル押下 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** OK押下 */
    async onOkClicked() {
      if (this.phase === PHASE.START) {
        this.inputDataProcess();
        return;
      }

      if (this.phase === PHASE.INPUT_DATA) {
        await this.validateWithVariantData();
        return;
      }

      if (this.phase === PHASE.INVALID_DATA
        || this.phase === PHASE.SUCCESS
        || this.phase === PHASE.WRITE_ERROR
        || this.phase === PHASE.FAILED) {
        this.closeWizard();
      }
    },

    /** terminate処理 */
    async terminate() {
      const response = await this.callApi('terminate');
      if (!response) {
        return false;
      }
      return response.data.status === REQUEST_STATUS_COMPLETED;
    },

    /** dispose処理 */
    async dispose() {
      const response = await this.callApi('dispose');
      if (!response) {
        return false;
      }
      return response.data.status === REQUEST_STATUS_COMPLETED;
    },

    /** 共通エラー後処理 */
    async exitProcess() {
      if (this.isExiting) {
        return;
      }
      this.isExiting = true;
      try {
        if (this.isSession) {
          await this.terminate();
        } else {
          await this.dispose();
        }
      } finally {
        this.isExiting = false;
      }
    },
  },

  async mounted() {
    this.init();
    await this.getVariantData();
  },
};
